import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import { AddCircleOutlineRounded } from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { adminService } from "../services/adminService";
import {
  ROUTES,
  buttonGradient,
  buttonGradientHover,
  buttonShadow,
} from "../../../constants/consts";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const EMPTY = {
  title: "",
  sunucuKodu: "",
  supporter: "",
  eduDate: "",
  duration: "",
  desc: "",
};

const normalize = (s = "") => String(s).toLocaleLowerCase("tr-TR").trim();

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    transition: "box-shadow 0.25s ease, background-color 0.25s ease",
    "&:hover": { backgroundColor: "rgba(4,87,129,0.02)" },
    "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(4,87,129,0.10)" },
  },
};

export default function AdminEduAdd() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [sunucuKoduList, setSunucuKoduList] = useState([]);
  const [personelList, setPersonelList] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({ open: false, msg: "" });

  useEffect(() => {
    (async () => {
      try {
        const [companies, supporters] = await Promise.all([
          adminService.getCompanies(),
          adminService.getUsersByType({ type: "destek", sunucuKodu: "" }),
        ]);
        setSunucuKoduList(companies.map((c) => c.sunucu_kodu).filter(Boolean));
        const names = supporters.map((u) => u.username).filter(Boolean);
        setPersonelList(names);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingOptions(false);
      }
    })();
  }, []);

  const handleChange = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.title.trim() ||
      !form.sunucuKodu ||
      !form.supporter ||
      !form.eduDate ||
      !form.duration ||
      !form.desc.trim()
    ) {
      setError("Tüm alanlar zorunludur.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await adminService.addEdu({
        title: form.title.trim(),
        sunucuKodu: form.sunucuKodu,
        supporter: form.supporter,
        eduDate: form.eduDate,
        duration: String(form.duration),
        desc: form.desc.trim(),
      });
      setSnack({ open: true, msg: "Eğitim başarıyla eklendi." });
      setForm(EMPTY);
      setTimeout(() => navigate(ROUTES.ADMIN_EDU), 900);
    } catch (err) {
      console.error(err);
      setError("Eğitim eklenemedi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        gap: 1.5,
      }}
    >
      {/* Başlık */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          animation: `${fadeInUp} 0.4s ease-out both`,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.14)",
            border: "1px solid rgba(255,255,255,0.20)",
          }}
        >
          <AddCircleOutlineRounded sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Box>
          <Typography
            variant="h6"
            fontWeight={800}
            color="#fff"
            sx={{ fontSize: 18 }}
          >
            Yeni Eğitim Ekle
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            Yeni bir eğitim kaydı oluşturun
          </Typography>
        </Box>
      </Box>

      {/* Form */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          overflowY: "auto",
          pt: 1,
          pb: 2,
        }}
      >
        <Paper
          elevation={0}
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: 3,
            backgroundColor: "rgba(255,255,255,0.98)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.20)",
            border: "1px solid rgba(255,255,255,0.30)",
            width: "100%",
            maxWidth: { xs: "100%", sm: 900, md: 1100 },
            animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s backwards`,
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: { xs: 2, sm: 2.5 },
            }}
          >
            <TextField
              label="Konu *"
              value={form.title}
              onChange={handleChange("title")}
              fullWidth
              sx={{ ...inputSx, gridColumn: { xs: "1", sm: "1 / span 2" } }}
            />

            <Autocomplete
              size="small"
              options={sunucuKoduList}
              value={form.sunucuKodu || null}
              onChange={(_, v) =>
                setForm((prev) => ({ ...prev, sunucuKodu: v || "" }))
              }
              disabled={loadingOptions}
              filterOptions={(opts, { inputValue }) =>
                opts.filter((o) => normalize(o).includes(normalize(inputValue)))
              }
              noOptionsText="Sunucu bulunamadı"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sunucu Kodu *"
                  placeholder="Yaz veya seç"
                  sx={inputSx}
                />
              )}
            />

            <Autocomplete
              size="small"
              options={personelList}
              value={form.supporter || null}
              onChange={(_, v) =>
                setForm((prev) => ({ ...prev, supporter: v || "" }))
              }
              disabled={loadingOptions}
              filterOptions={(opts, { inputValue }) =>
                opts.filter((o) => normalize(o).includes(normalize(inputValue)))
              }
              noOptionsText="Personel bulunamadı"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Destek Personeli *"
                  placeholder="Yaz veya seç"
                  sx={inputSx}
                />
              )}
            />

            <TextField
              label="Eğitim Tarihi *"
              type="date"
              value={form.eduDate}
              onChange={handleChange("eduDate")}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />

            <TextField
              label="Süre (saat) *"
              type="number"
              value={form.duration}
              onChange={handleChange("duration")}
              fullWidth
              inputProps={{ min: 0, step: 1 }}
              sx={inputSx}
            />

            <TextField
              label="Açıklama *"
              value={form.desc}
              onChange={handleChange("desc")}
              fullWidth
              multiline
              minRows={4}
              maxRows={10}
              sx={{ ...inputSx, gridColumn: { xs: "1", sm: "1 / span 2" } }}
            />
          </Box>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              gap: 1.5,
              justifyContent: "flex-end",
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate(ROUTES.ADMIN_EDU)}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                py: 1.2,
                fontWeight: 600,
              }}
            >
              Vazgeç
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 700,
                px: 4,
                py: 1.2,
                background: buttonGradient,
                boxShadow: buttonShadow,
                "&:hover": { background: buttonGradientHover },
                "&:disabled": { background: "#c7c7c7" },
              }}
            >
              {saving ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Kaydet"
              )}
            </Button>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack({ open: false, msg: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
